import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Store, Pause, BadgeDollarSign, Package, X, Printer, Trash2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "../console/api";

/* Available to Sell , the live remainder of the Tuber Audit.
   Final audit − customer orders − growing folders − reserved = available. */

const EDGE = "#E8DFCB";
const AUB = "#3E1F46";
const btn = "inline-flex items-center gap-1.5 px-3 h-9 text-[10px] font-bold uppercase tracking-[0.12em]";

export default function AdminAvailableToSell() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [acting, setActing] = useState(null);
  const [orders, setOrders] = useState([]);

  const load = async () => {
    try {
      const r = await api.get("/admin/allocation/available-to-sell");
      setData(r.data);
    } catch { toast.error("Couldn't load the sell list."); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    api.get("/console/orders", { params: { stage: "assigned" } })
      .then((r) => setOrders(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  const rows = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const list = data?.rows || [];
    return needle ? list.filter((r) => r.name.toLowerCase().includes(needle)) : list;
  }, [data, filter]);

  const release = async (id) => {
    try {
      await api.delete(`/admin/allocation/reserve/${id}`);
      toast.success("Released back to available.");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't release that."); }
  };

  const sendToStore = async (row) => {
    try {
      const r = await api.post(`/admin/allocation/reserve/${row.id}/send-to-store`);
      toast.success(`${r.data.qty} × ${r.data.name} in store stock at NZ$${r.data.price.toFixed(2)}.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send that to the store."); }
  };

  const setPrice = async (row, price) => {
    try {
      await api.patch(`/admin/allocation/reserve/${row.id}`, { price: Number(price) });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save that price."); }
  };

  if (loading) return <div className="text-[#7A6A55] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading sell list…</div>;
  if (!data) return null;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="admin-available-to-sell">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.42em] uppercase font-semibold text-[#6E1E3A]">✦ Live remainder</div>
          <h1 className="font-serif text-4xl sm:text-5xl mt-1 text-[#3C2A1E]">Available to Sell</h1>
          <p className="text-sm italic text-[#7A6A55] mt-1 max-w-2xl">
            Final audit minus customer orders, growing folders and anything reserved. Change an allocation anywhere and this drops instantly.
          </p>
        </div>
        <button type="button" onClick={() => printSellList(rows)} disabled={rows.length === 0} data-testid="sell-print"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#6E1E3A] text-white text-[11px] font-bold uppercase tracking-[0.14em] disabled:opacity-40">
          <Printer className="w-4 h-4" /> Print sell list
        </button>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[["Audited", data.summary.total_audited, AUB], ["To orders", data.summary.total_orders, "#6E1E3A"],
          ["To growing", data.summary.total_growing, "#2F6B4F"], ["Available", data.summary.total_available, "#1F5B3F"]].map(([l, v, c]) => (
          <div key={l} className="bg-white border rounded p-3" style={{ borderColor: EDGE }}>
            <div className="text-[9px] tracking-[0.22em] uppercase text-[#9C8B73]">{l}</div>
            <div className="font-serif text-2xl mt-0.5" style={{ color: c }} data-testid={`sell-stat-${l.toLowerCase().replace(/\s+/g, "-")}`}>{v}</div>
          </div>
        ))}
      </div>

      <section className="bg-white border rounded overflow-x-auto" style={{ borderColor: EDGE }} data-testid="sell-table">
        <div className="px-3 py-2 border-b flex items-center gap-2" style={{ borderColor: EDGE }}>
          <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A]">Every variety</div>
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter…" data-testid="sell-filter"
            className="ml-auto h-8 px-2 border rounded text-sm outline-none" style={{ borderColor: EDGE }} />
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[9px] tracking-[0.2em] uppercase text-[#9C8B73]">
              <th className="text-left px-3 py-2">Variety</th>
              <th className="px-2 py-2">Final</th>
              <th className="px-2 py-2">Orders</th>
              <th className="px-2 py-2">Growing</th>
              <th className="px-2 py-2">Reserved</th>
              <th className="px-2 py-2">Available</th>
              <th className="px-3 py-2 text-right">Do something</th>
            </tr>
          </thead>
          <tbody className="divide-y" style={{ borderColor: EDGE }}>
            {rows.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center text-[#7A6A55]" data-testid="sell-empty">Nothing on the audit yet.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.variety_key} data-testid={`sell-row-${r.variety_key}`}>
                <td className="px-3 py-2 font-serif text-[15px] text-[#3C2A1E]">{r.name}</td>
                <td className="px-2 py-2 text-center">{r.final}</td>
                <td className="px-2 py-2 text-center">{r.orders}</td>
                <td className="px-2 py-2 text-center">{r.growing}</td>
                <td className="px-2 py-2 text-center">{r.reserved}</td>
                <td className="px-2 py-2 text-center font-bold" style={{ color: r.available > 0 ? "#1F5B3F" : "#9C8B73" }} data-testid={`sell-available-${r.variety_key}`}>{r.available}</td>
                <td className="px-3 py-2 text-right">
                  <button type="button" disabled={r.available === 0} onClick={() => setActing(r)} data-testid={`sell-action-${r.variety_key}`}
                    className={btn + " text-white disabled:opacity-30"} style={{ backgroundColor: "#6E1E3A" }}>
                    <Store className="w-3.5 h-3.5" /> Sell · hold · sold
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <ReserveList title="Sell list , price it, then push to store stock" rows={data.sell_list} testid="sell-list"
        empty="Nothing on the sell list yet." onRelease={release} onPrice={setPrice} onStore={sendToStore} />
      <ReserveList title="Held back" rows={data.held} testid="held-list" empty="Nothing held back." onRelease={release} />
      <ReserveList title="Sold" rows={data.sold} testid="sold-list" empty="Nothing sold yet." onRelease={release} />

      {acting && (
        <ActionModal row={acting} orders={orders} onClose={() => setActing(null)}
          onDone={async () => { setActing(null); await load(); }} />
      )}
    </div>
  );
}

const KIND_LABEL = { sell: "Sell list", hold: "Held", sold: "Sold", order: "Reserved for order" };

const ReserveList = ({ title, rows, empty, testid, onRelease, onPrice, onStore }) => (
  <section className="space-y-2" data-testid={testid}>
    <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A]">{title}</div>
    {(rows || []).length === 0 ? (
      <div className="bg-white border border-dashed rounded p-5 text-center text-sm text-[#7A6A55]" style={{ borderColor: EDGE }}>{empty}</div>
    ) : rows.map((r) => (
      <div key={r.id} className="bg-white border rounded p-3 flex flex-wrap items-center gap-x-3 gap-y-2" style={{ borderColor: EDGE }} data-testid={`${testid}-${r.id}`}>
        <strong className="font-serif text-[16px] text-[#3C2A1E]">{r.qty} × {r.name}</strong>
        <span className="text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded bg-[#F3EBDD] text-[#6E1E3A] font-semibold">
          {r.status === "in_store" ? "In store stock" : KIND_LABEL[r.kind] || r.kind}
        </span>
        {r.buyer && <span className="text-xs text-[#7A6A55]">{r.buyer}</span>}
        {r.note && <span className="text-xs italic text-[#9C8B73]">{r.note}</span>}
        {onPrice && (
          <label className="flex items-center gap-1.5 text-xs text-[#7A6A55]">
            NZ$
            <input type="number" min="0" step="0.5" defaultValue={r.price ?? ""} placeholder="price"
              onBlur={(e) => e.target.value !== "" && onPrice(r, e.target.value)} data-testid={`sell-price-${r.id}`}
              className="w-20 h-9 px-2 border rounded text-sm" style={{ borderColor: EDGE }} />
          </label>
        )}
        <div className="ml-auto flex gap-1.5">
          {onStore && r.status !== "in_store" && (
            <button type="button" onClick={() => onStore(r)} data-testid={`sell-to-store-${r.id}`}
              className={btn + " text-white"} style={{ backgroundColor: "#2F6B4F" }}>
              <Send className="w-3.5 h-3.5" /> Send to store stock
            </button>
          )}
          <button type="button" onClick={() => onRelease(r.id)} data-testid={`release-${r.id}`}
            className={btn + " border border-[#A33030] text-[#A33030]"}>
            <Trash2 className="w-3.5 h-3.5" /> Release
          </button>
        </div>
      </div>
    ))}
  </section>
);

const ActionModal = ({ row, orders, onClose, onDone }) => {
  const [kind, setKind] = useState("sell");
  const [qty, setQty] = useState("");
  const [price, setPrice] = useState("");
  const [buyer, setBuyer] = useState("");
  const [note, setNote] = useState("");
  const [orderId, setOrderId] = useState(orders[0]?.id || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const n = parseInt(qty, 10);
    if (!Number.isFinite(n) || n <= 0) { toast.error("How many tubers?"); return; }
    setBusy(true);
    try {
      await api.post("/admin/allocation/reserve", {
        name: row.name, qty: n, kind,
        price: price === "" ? null : Number(price),
        buyer, note, order_id: kind === "order" ? orderId : null,
      });
      toast.success(`${n} × ${row.name} → ${KIND_LABEL[kind]}.`);
      await onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't do that."); }
    finally { setBusy(false); }
  };

  const kinds = [
    ["sell", "Sell now", Store],
    ["hold", "Hold", Pause],
    ["sold", "Sold", BadgeDollarSign],
    ["order", "Add to custom order", Package],
  ];

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 grid place-items-center p-4" onClick={onClose} data-testid="sell-action-modal">
      <div className="bg-[#F8F4EA] border-2 w-full max-w-sm p-5 space-y-3" style={{ borderColor: AUB }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A]">Available {row.available}</div>
            <h3 className="font-serif text-2xl text-[#3C2A1E]">{row.name}</h3>
          </div>
          <button type="button" onClick={onClose} data-testid="sell-action-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {kinds.map(([k, label, Icon]) => (
            <button key={k} type="button" onClick={() => setKind(k)} data-testid={`sell-kind-${k}`}
              className="h-11 px-2 border text-[10px] font-bold uppercase tracking-[0.1em] inline-flex items-center justify-center gap-1.5"
              style={kind === k ? { backgroundColor: AUB, borderColor: AUB, color: "#fff" } : { borderColor: EDGE, color: "#3C2A1E" }}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>
        <input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="Quantity"
          data-testid="sell-qty" className="w-full h-11 px-3 border rounded text-sm" style={{ borderColor: EDGE }} />
        {(kind === "sell" || kind === "sold") && (
          <input type="number" min="0" step="0.5" value={price} onChange={(e) => setPrice(e.target.value)}
            placeholder="Price each (NZ$)" data-testid="sell-price-input"
            className="w-full h-11 px-3 border rounded text-sm" style={{ borderColor: EDGE }} />
        )}
        {kind === "sold" && (
          <input value={buyer} onChange={(e) => setBuyer(e.target.value)} placeholder="Who bought them?"
            data-testid="sell-buyer" className="w-full h-11 px-3 border rounded text-sm" style={{ borderColor: EDGE }} />
        )}
        {kind === "order" && (
          <select value={orderId} onChange={(e) => setOrderId(e.target.value)} data-testid="sell-order"
            className="w-full h-11 px-2 border rounded bg-white text-sm" style={{ borderColor: EDGE }}>
            {orders.length === 0 ? <option value="">No packing orders</option> : orders.map((o) => (
              <option key={o.id} value={o.id}>{o.customer_name || "Customer"} · #{o.order_number || String(o.id).slice(0, 8)}</option>
            ))}
          </select>
        )}
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)"
          data-testid="sell-note" className="w-full h-11 px-3 border rounded text-sm" style={{ borderColor: EDGE }} />
        <p className="text-[11px] italic text-[#7A6A55]">
          {kind === "order"
            ? "Reserves the tubers against that order , the order's own items aren't touched."
            : "Reserved tubers leave Available to Sell straight away."}
        </p>
        <button type="button" onClick={submit} disabled={busy} data-testid="sell-action-save"
          className="w-full h-12 text-white text-[11px] font-bold uppercase tracking-[0.14em] disabled:opacity-40"
          style={{ backgroundColor: "#6E1E3A" }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : `Save , ${KIND_LABEL[kind]}`}
        </button>
      </div>
    </div>
  );
};

function printSellList(rows) {
  const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;");
  const trs = rows.filter((r) => r.available > 0).map((r) => `
    <tr><td>${esc(r.name)}</td><td class="c">${r.final}</td><td class="c">${r.orders}</td><td class="c">${r.growing}</td><td class="c">${r.reserved}</td><td class="c b">${r.available}</td></tr>`).join("");
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(`<html><head><title>Available to sell</title><style>
    body{font-family:Georgia,serif;color:#3C2A1E;margin:30px;}
    h1{font-size:24px;margin:0;color:#3E1F46;}
    .meta{font-size:12px;opacity:.7;margin:4px 0 16px;}
    table{border-collapse:collapse;width:100%;font-size:13px;}
    th{text-align:left;font-size:10px;letter-spacing:.22em;text-transform:uppercase;border-bottom:2px solid #C9A268;padding:6px 8px;color:#6E1E3A;}
    td{border-bottom:1px solid #E5D9C5;padding:6px 8px;}
    .c{text-align:center;} .b{font-weight:bold;font-size:15px;}
  </style></head><body>
    <h1>Heritage Dahlias — Available to Sell</h1>
    <div class="meta">${new Date().toLocaleString("en-NZ")}</div>
    <table><thead><tr><th>Variety</th><th class="c">Final</th><th class="c">Orders</th><th class="c">Growing</th><th class="c">Reserved</th><th class="c">Available</th></tr></thead>
    <tbody>${trs}</tbody></table>
  </body></html>`);
  w.document.close();
}
